import { useEffect, useState, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  getSession,
  login,
  canAttemptSilentReauth,
  beginSilentReauth,
  clearSilentReauthFlag,
  type SessionState,
} from './lib/api.js';
import { settingsApi } from './lib/data.js';
import { setDisplayCurrency } from './lib/format.js';
import { AppShell } from './components/AppShell.js';
import { Dashboard } from './routes/Dashboard.js';
import { Budget } from './routes/Budget.js';
import { NetWorth } from './routes/NetWorth.js';
import { Projection } from './routes/Projection.js';
import { LoanCalculator } from './routes/LoanCalculator.js';
import { Loans } from './routes/Loans.js';
import { Assets } from './routes/Assets.js';
import { Travel } from './routes/Travel.js';
import { Receivables } from './routes/Receivables.js';
import { Settings } from './routes/Settings.js';
import { Saxo } from './routes/Saxo.js';

export function App(): JSX.Element {
  const [session, setSession] = useState<SessionState | null>(null);
  const [reauthing, setReauthing] = useState(false);
  // Gate the authed app until the display currency is loaded, so every page formats money
  // in the chosen currency on first paint (no DKK flash for non-DKK users).
  const [currencyReady, setCurrencyReady] = useState(false);

  useEffect(() => {
    // ?li=1 marker from the OIDC callback → give the Set-Cookie a beat, then probe.
    const justLoggedIn = new URLSearchParams(window.location.search).has('li');
    const run = () =>
      getSession().then((s) => {
        if (s.authenticated) {
          clearSilentReauthFlag();
        } else if (canAttemptSilentReauth()) {
          // Session expired but HeivolID likely still holds a live sign-in (24h JWT) —
          // bounce through /auth/login once instead of showing the login screen.
          setReauthing(true);
          beginSilentReauth();
          return; // navigating away; keep the transient screen up
        }
        setSession(s);
        if (justLoggedIn) window.history.replaceState({}, '', window.location.pathname);
      });
    if (justLoggedIn) setTimeout(run, 150);
    else void run();
  }, []);

  // Once authenticated, load the display currency before showing the authed app.
  useEffect(() => {
    if (!session?.authenticated) return;
    settingsApi
      .load()
      .then((s) => setDisplayCurrency(s.currency))
      .catch(() => {
        /* fall back to the default (DKK) already set in format.ts */
      })
      .finally(() => setCurrencyReady(true));
  }, [session?.authenticated]);

  if (reauthing) {
    return <FullScreen>Restoring login…</FullScreen>;
  }

  if (session === null) {
    return <FullScreen>Loading…</FullScreen>;
  }

  if (!session.authenticated) {
    return <LoginScreen />;
  }

  if (!currencyReady) {
    return <FullScreen>Loading…</FullScreen>;
  }

  return (
    <AppShell email={session.user?.email ?? null}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/budget" element={<Budget />} />
        <Route path="/net-worth" element={<NetWorth />} />
        <Route path="/projection" element={<Projection />} />
        <Route path="/loan-calculator" element={<LoanCalculator />} />
        <Route path="/loans" element={<Loans />} />
        <Route path="/travel" element={<Travel />} />
        <Route path="/receivables" element={<Receivables />} />
        {/* /aktiver, not /assets — the Vite build emits a physical `assets/` dir under the
            web root, which nginx would serve (→ 403) instead of falling back to the SPA. */}
        <Route path="/aktiver" element={<Assets />} />
        <Route path="/assets" element={<Navigate to="/aktiver" replace />} />
        <Route path="/goals" element={<Navigate to="/projection" replace />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/saxo" element={<Saxo />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

function FullScreen({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center text-hv-text-secondary">{children}</div>
  );
}

function LoginScreen(): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center bg-hv-bg p-6">
      <div className="w-full max-w-sm rounded-hv-lg border border-hv-border bg-hv-surface p-8 text-center shadow-hv-md">
        <div className="mb-2 text-2xl font-medium text-hv-text">Heivol Finance</div>
        <p className="mb-6 text-hv-sm text-hv-text-secondary">
          Your budget, your net worth, and when you can retire — under Danish rules.
        </p>
        <button
          onClick={login}
          className="w-full rounded-hv-md bg-hv-ai px-4 py-2.5 font-medium text-hv-text-inverse transition-colors hover:bg-hv-ai-hover focus-visible:shadow-hv-focus"
        >
          Sign in with HeivolID
        </button>
        <p className="mt-4 text-hv-xs text-hv-text-tertiary">MitID via HeivolID · no separate account</p>
      </div>
    </div>
  );
}
